class TicketThread extends HTMLElement {
  connectedCallback() {
    this.apiBase = (this.dataset.apiBase || '').replace(/\/$/, '');
    this.statusEl = this.querySelector('[data-thread-status]');
    this.loadingEl = this.querySelector('[data-thread-loading]');
    this.contentEl = this.querySelector('[data-thread-content]');
    this.helpEl = this.querySelector('[data-thread-help]');
    this.messagesEl = this.querySelector('[data-thread-messages]');
    this.replyForm = this.querySelector('[data-thread-reply-form]');
    this.replyInput = this.querySelector('[data-thread-reply-input]');
    this.closedNote = this.querySelector('[data-thread-closed-note]');
    this.tokenForm = this.querySelector('[data-token-form]');
    this.tokenInput = this.querySelector('[data-token-input]');

    this.token = this.resolveToken();

    if (this.replyForm) {
      this.replyForm.addEventListener('submit', this.onReply.bind(this));
    }
    if (this.tokenForm) {
      this.tokenForm.addEventListener('submit', this.onTokenSubmit.bind(this));
    }

    if (!this.token) {
      this.showHelp(true);
      this.showError(this.dataset.missingToken || 'Länken saknar ärendenyckel.');
      return;
    }

    this.persistToken(this.token);
    this.showHelp(false);
    this.load();
  }

  resolveToken() {
    const params = new URLSearchParams(window.location.search);
    let token = (params.get('token') || '').trim();

    // Handle mangled links like ?token%3Dabc (double-encoded "=")
    if (!token) {
      for (const [key, value] of params.entries()) {
        if (key.toLowerCase().startsWith('token=')) {
          token = key.slice(6) || value;
          break;
        }
        if (key.toLowerCase() === 'token' && value) {
          token = value;
          break;
        }
      }
    }

    if (!token) {
      const match = window.location.href.match(/[?&#]token(?:=|%3D)([a-f0-9]{16,})/i);
      if (match) token = match[1];
    }

    if (!token) {
      try {
        token = sessionStorage.getItem('rfrb_ticket_token') || '';
      } catch (e) {
        /* ignore */
      }
    }

    return String(token || '').trim();
  }

  persistToken(token) {
    try {
      sessionStorage.setItem('rfrb_ticket_token', token);
    } catch (e) {
      /* ignore */
    }
    // Keep token visible in address bar for sharing/reloads
    const url = new URL(window.location.href);
    if (url.searchParams.get('token') !== token) {
      url.searchParams.set('token', token);
      window.history.replaceState({}, '', url.toString());
    }
  }

  onTokenSubmit(event) {
    event.preventDefault();
    const raw = String(this.tokenInput?.value || '').trim();
    const match = raw.match(/([a-f0-9]{16,})/i);
    const token = match ? match[1] : raw;
    if (!token || token.length < 16) {
      this.showError('Klistra in token från mejllänken (den långa koden efter token=).');
      return;
    }
    this.token = token;
    this.persistToken(token);
    this.showError('');
    this.showHelp(false);
    this.load();
  }

  showHelp(visible) {
    if (this.helpEl) this.helpEl.hidden = !visible;
  }

  showError(message) {
    if (!this.statusEl) return;
    if (!message) {
      this.statusEl.hidden = true;
      this.statusEl.textContent = '';
      return;
    }
    this.statusEl.hidden = false;
    this.statusEl.textContent = message;
  }

  setLoading(isLoading) {
    if (this.loadingEl) this.loadingEl.hidden = !isLoading;
  }

  renderConversation(text) {
    if (!this.messagesEl) return;
    this.messagesEl.innerHTML = '';
    const chunks = String(text || '')
      .split(/\n\n---\n\n/)
      .map((s) => s.trim())
      .filter(Boolean);

    if (!chunks.length) {
      const empty = document.createElement('p');
      empty.className = 'ticket-thread__empty';
      empty.textContent = 'Inga meddelanden ännu.';
      this.messagesEl.appendChild(empty);
      return;
    }

    chunks.forEach((chunk) => {
      const isSupport = /^\[.+\]\s*Support:/i.test(chunk);
      const isCustomer = /^\[.+\]\s*Kund:/i.test(chunk);
      const article = document.createElement('article');
      article.className = `ticket-thread__bubble ticket-thread__bubble--${
        isSupport ? 'support' : isCustomer ? 'customer' : 'other'
      }`;
      article.textContent = chunk;
      this.messagesEl.appendChild(article);
    });
  }

  applyTicket(ticket) {
    const numberEl = this.querySelector('[data-thread-number]');
    const subjectEl = this.querySelector('[data-thread-subject]');
    const statusEl = this.querySelector('[data-thread-ticket-status]');
    if (numberEl) numberEl.textContent = ticket.ticketNumber || '';
    if (subjectEl) subjectEl.textContent = ticket.subject || '';
    if (statusEl) statusEl.textContent = ticket.status || '';

    this.renderConversation(ticket.conversation);

    const closed = Boolean(ticket.closed || ticket.status === 'Stängd');
    if (this.replyForm) this.replyForm.hidden = closed;
    if (this.closedNote) this.closedNote.hidden = !closed;
  }

  async load() {
    this.showError('');
    this.setLoading(true);
    this.showHelp(false);
    if (this.contentEl) this.contentEl.hidden = true;

    try {
      if (!this.apiBase) {
        this.showError(
          this.dataset.networkError ||
            'Ärendetjänsten saknar API-URL. Sätt den i temat (Kundärenden).'
        );
        this.showHelp(true);
        return;
      }

      const response = await fetch(
        `${this.apiBase}/api/arende/thread?token=${encodeURIComponent(this.token)}`,
        { headers: { Accept: 'application/json' } }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) {
        this.showError(data.error || this.dataset.genericError || 'Kunde inte hämta ärendet.');
        this.showHelp(true);
        return;
      }
      this.applyTicket(data.ticket);
      if (this.contentEl) this.contentEl.hidden = false;
    } catch (error) {
      console.error(error);
      this.showError(
        this.dataset.networkError ||
          'Kunde inte nå ärendetjänsten. Kontrollera att API:t körs (port 8787).'
      );
      this.showHelp(true);
    } finally {
      this.setLoading(false);
    }
  }

  async onReply(event) {
    event.preventDefault();
    const message = String(this.replyInput?.value || '').trim();
    if (!message) return;

    const button = this.replyForm.querySelector('[type="submit"]');
    if (button) button.disabled = true;
    this.showError('');

    try {
      const response = await fetch(`${this.apiBase}/api/arende/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ token: this.token, message }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) {
        this.showError(data.error || this.dataset.genericError || 'Kunde inte skicka svaret.');
        return;
      }
      if (this.replyInput) this.replyInput.value = '';
      if (data.conversation) this.renderConversation(data.conversation);
      else await this.load();
    } catch (error) {
      console.error(error);
      this.showError(this.dataset.networkError || 'Kunde inte nå ärendetjänsten.');
    } finally {
      if (button) button.disabled = false;
    }
  }
}

customElements.define('ticket-thread', TicketThread);
